import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Perfil } from 'src/app/services/auth/perfil.enum';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from 'src/environments/app-config.service';
import { PsaAuthorizationService } from 'src/app/services/auth/psa-authorization.service';
import jwtDecode from 'jwt-decode';
import { B3MenuItem } from 'src/app/shared/models/b3-menu-item.model';
import {
  HomeActionColumn,
  HomeActionItem,
  HomeActionSubsection,
  HomeSearchGroup,
} from 'src/app/pages/home/models/home.models';
import {
  HOME_COLUMN_TITLES,
  HOME_EXCLUDED_LINKS,
  HOME_LINK_PRESENTATION,
  HOME_ROUTE,
  HOME_SEARCH_GROUPS,
  HOME_SEARCH_LINK_PRESENTATION,
} from 'src/app/pages/home/home-menu.config';
import {
  CarSidebarChildItem,
  CarSidebarFooterItem,
  CarSidebarGroupItem,
  CarSidebarHomeItem,
  CarSidebarLinkItem,
  CarSidebarNav,
  CarSidebarSectionItem,
} from '../car-sidebar.model';
import { AuthorizationUserProfile } from '../../platform-header/platform-header-user.model';
import {
  AuthorizationFunction,
  AuthorizationsResponse,
} from 'src/app/shared/interfaces/authorizations-response';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private environment: any;
  private readonly apiUrl: string;
  private acxPlataformRedirectUrl: string;

  /** @deprecated Mantido para compatibilidade; preferir sidebarNav$. */
  menuItens: BehaviorSubject<B3MenuItem[]> = new BehaviorSubject([] as B3MenuItem[]);

  sidebarNav$ = new BehaviorSubject<CarSidebarNav | null>(null);
  sidebarFooter$ = new BehaviorSubject<CarSidebarFooterItem[]>([]);

  private readonly menuLoadedSubject = new BehaviorSubject<boolean>(false);

  readonly menuLoaded$ = this.menuLoadedSubject.asObservable();

  userProfile$ = new BehaviorSubject<AuthorizationUserProfile | null>(null);

  registroProjetoChildren: B3MenuItem[] = [];
  emissaoCreditoChildren: B3MenuItem[] = [];
  registroCreditoChildren: B3MenuItem[] = [];
  operacaoCreditoChildren: B3MenuItem[] = [];
  posicaoCreditoChildren: B3MenuItem[] = [];

  constructor(
    private readonly http: HttpClient,
    private readonly appConfig: AppConfigService,
    private readonly psaAuthorizationService: PsaAuthorizationService,
  ) {
    this.environment = this.appConfig.config;
    this.apiUrl = this.environment.apiIssuer;
    this.acxPlataformRedirectUrl = this.environment.acxPlataformRedirectUrl;
  }

  getOptionsMenu(url: string, acxPlataformRedirectUrl: string, force = false): void {
    if (!force && this.isMenuLoaded()) {
      return;
    }

    this.acxPlataformRedirectUrl = acxPlataformRedirectUrl;
    const documentCode = this.formatDocument();

    this.menuLoadedSubject.next(false);

    this.http.get<AuthorizationsResponse>(`${url}/authorizations/${documentCode}`).subscribe(json => {
      this.menuBuilder(json);
    });
  }

  isMenuLoaded(): boolean {
    return sessionStorage.getItem('MENU_LOADED') === 'true' && this.sidebarNav$.getValue() !== null;
  }

  getAcxPlataformRedirectUrl(): string {
    return this.acxPlataformRedirectUrl ?? '';
  }

  openAcxPlatform(target = '_blank'): void {
    if (!this.acxPlataformRedirectUrl) {
      return;
    }

    window.open(this.acxPlataformRedirectUrl, target, 'noopener,noreferrer');
  }

  whenMenuLoaded$(): Observable<boolean> {
    if (this.isMenuLoaded()) {
      return of(true);
    }

    return this.menuLoaded$.pipe(
      filter(() => this.isMenuLoaded()),
      take(1),
    );
  }

  getDecodedAcessToken(): any {
    try {
      const token = sessionStorage.getItem('PING_ACCESS_TOKEN');
      return jwtDecode(`${token}`);
    } catch {
      return null;
    }
  }

  formatDocument(): any {
    const json = this.getDecodedAcessToken();
    const documentCode = json.cn;
    return documentCode.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '\$1.\$2.\$3\-\$4');
  }

  resetMenuVariables(): void {
    this.userProfile$.next(null);
    this.registroProjetoChildren = [];
    this.emissaoCreditoChildren = [];
    this.registroCreditoChildren = [];
    this.operacaoCreditoChildren = [];
    this.posicaoCreditoChildren = [];
  }

  menuBuilder(data: AuthorizationsResponse): void {
    this.resetMenuVariables();

    this.userProfile$.next({
      personName: data?.data?.personName ?? '',
      socialName: data?.data?.socialName ?? null,
    });

    data.data.functions = Array.from(
      new Map(data.data.functions.map((item: AuthorizationFunction) => [item.functionCode, item])).values(),
    );

    const psaFunctions = data.data.functions.filter((val: AuthorizationFunction) =>
      val.functionCode === Perfil.CAR_PSA_REGISTRA_PROJETO
      || val.functionCode === Perfil.CAR_PSA_AUDITA_PROJETO
      || val.functionCode === Perfil.CAR_PSA_CONSULTA_PROJETO,
    );
    this.psaAuthorizationService.setFunctions(psaFunctions);

    if (data.data.internalUserIndicator !== undefined && data.data.internalUserIndicator !== null) {
      sessionStorage.setItem('INTERNAL_USER_INDICATOR', String(data.data.internalUserIndicator));
    }

    if (data.data.functions) {
      data.data.functions.forEach((value: AuthorizationFunction) => {
        this.menuPermissionsAndBuilder(value.functionCode);
      });

      this.compactChildrenArrays();
      this.publishSidebarNav();
    }

    sessionStorage.setItem('MENU_LOADED', 'true');
    this.menuLoadedSubject.next(true);
  }

  menuPermissionsAndBuilder(functionCode: string): void {
    switch (functionCode) {
      case Perfil.CAR_REGISTRO_CREDITO_DE_CARBONO:
        this.registroCreditoChildren[0] = this.menuItemBuilder('Registro', 'registro-credito');
        break;
      case Perfil.CAR_CONSULTA_CREDITO_DE_CARBONO:
        this.registroCreditoChildren[2] = this.menuItemBuilder('Consulta', 'consulta-caracteristicas');
        break;
      case Perfil.CAR_CONFIRMACAO_DE_REGISTRO_CREDITO_DE_CARBONO:
        this.registroCreditoChildren[1] = this.menuItemBuilder('Confirmação', 'registro-confirmacao');
        break;
      case Perfil.CAR_TRANSFERENCIA_CREDITO_DE_CARBONO:
        this.operacaoCreditoChildren[2] = this.menuItemBuilder('Transferência', 'operacao-transferencia');
        break;
      case Perfil.CAR_RETIRADA_CREDITO_DE_CARBONO:
        this.operacaoCreditoChildren[3] = this.menuItemBuilder('Retirada', 'operacao-retirada');
        break;
      case Perfil.CAR_CONSULTA_OPERACOES_CREDITO_DE_CARBONO:
        this.operacaoCreditoChildren[1] = this.menuItemBuilder('Consulta', 'consulta-operacoes');
        break;
      case Perfil.CAR_CONFIRMA_OPERACOES_CREDITO_DE_CARBONO:
        this.operacaoCreditoChildren[0] = this.menuItemBuilder('Confirmação', 'operacoes-confirmacao');
        break;
      case Perfil.CAR_CONSULTA_OPERACOES_POSICAO_CREDITO_DE_CARBONO:
        this.posicaoCreditoChildren[0] = this.menuItemBuilder('Consulta', 'consulta-posicao');
        break;
      case Perfil.CAR_PSA_REGISTRA_PROJETO:
        this.registroProjetoChildren[0] = this.menuItemBuilder('Registro', 'registro-projeto');
        this.emissaoCreditoChildren[0] = this.menuItemBuilder('Emissão', 'registro-emissao-credito');
        this.emissaoCreditoChildren[2] = this.menuItemBuilder('Consulta', 'consulta-emissao-credito');
        break;
      case Perfil.CAR_PSA_CONSULTA_PROJETO:
        this.registroProjetoChildren[2] = this.menuItemBuilder('Consulta', 'consulta-projeto');
        this.emissaoCreditoChildren[2] = this.menuItemBuilder('Consulta', 'consulta-emissao-credito');
        break;
      case Perfil.CAR_PSA_AUDITA_PROJETO:
        this.registroProjetoChildren[1] = this.menuItemBuilder('Validação', 'validacao-projeto-credito');
        this.emissaoCreditoChildren[1] = this.menuItemBuilder('Verificação', 'confirma-emissao-credito');
        break;
    }
  }

  menuItemBuilder(title: string, link: string): B3MenuItem {
    return { title, link };
  }

  private compactChildrenArrays(): void {
    this.registroProjetoChildren = this.registroProjetoChildren.filter(Boolean);
    this.emissaoCreditoChildren = this.emissaoCreditoChildren.filter(Boolean);
    this.registroCreditoChildren = this.registroCreditoChildren.filter(Boolean);
    this.operacaoCreditoChildren = this.operacaoCreditoChildren.filter(Boolean);
    this.posicaoCreditoChildren = this.posicaoCreditoChildren.filter(Boolean);
  }

  private publishSidebarNav(): void {
    const groups: CarSidebarGroupItem[] = [];
    const creditOperationsGroup = this.buildCreditOperationsGroup();

    if (creditOperationsGroup) {
      groups.push(creditOperationsGroup);
    }

    const projectsGroup = this.buildProjectsEmissionGroup();
    if (projectsGroup) {
      groups.push(projectsGroup);
    }

    const home: CarSidebarHomeItem = {
      type: 'home',
      id: 'home',
      label: 'Página inicial',
      icon: 'home',
      link: HOME_ROUTE,
    };

    const nav: CarSidebarNav = { home, groups };
    this.sidebarNav$.next(nav);
    this.sidebarFooter$.next(this.buildFooterItems());
    this.menuItens.next(this.toLegacyMenuItems(nav));
  }

  private buildCreditOperationsGroup(): CarSidebarGroupItem | null {
    const children: CarSidebarChildItem[] = [];

    this.appendCreditOperationsDropdownLinks(children);

    if (!children.length) {
      return null;
    }

    return {
      type: 'group',
      id: 'credit-operations',
      label: 'Operações de crédito',
      icon: 'arrow_circle_down',
      expanded: true,
      children,
    };
  }

  private appendCreditOperationsDropdownLinks(children: CarSidebarChildItem[]): void {
    this.appendCreditOperationsSection(children);
    this.appendCreditConsultationsSection(children);
  }

  private appendCreditOperationsSection(children: CarSidebarChildItem[]): void {
    const operationLinks: Array<{
      id: string;
      label: string;
      link: string;
      isAllowed: () => boolean;
    }> = [
      {
        id: 'registrar',
        label: 'Registrar',
        link: 'registro-credito',
        isAllowed: () => this.hasRegistrationLink('registro-credito'),
      },
      {
        id: 'confirmacao-credito',
        label: 'Confirmar Crédito',
        link: 'registro-confirmacao',
        isAllowed: () => this.hasRegistrationLink('registro-confirmacao'),
      },
      {
        id: 'confirmacao-operacao',
        label: 'Confirmar Operação',
        link: 'operacoes-confirmacao',
        isAllowed: () => this.hasOperationLink('operacoes-confirmacao'),
      },
      {
        id: 'transferir',
        label: 'Transferir',
        link: 'operacao-transferencia',
        isAllowed: () => this.hasOperationLink('operacao-transferencia'),
      },
      {
        id: 'retirar',
        label: 'Retirar',
        link: 'operacao-retirada',
        isAllowed: () => this.hasOperationLink('operacao-retirada'),
      },
    ];

    const allowedLinks = operationLinks.filter(item => item.isAllowed());
    if (!allowedLinks.length) {
      return;
    }

    children.push(this.sectionItem('operacoes-section', 'Operações'));
    allowedLinks.forEach(item => {
      children.push(this.toLinkItem(item.id, item.label, item.link));
    });
  }

  private appendCreditConsultationsSection(children: CarSidebarChildItem[]): void {
    const consultationLinks: Array<{
      id: string;
      label: string;
      link: string;
      isAllowed: () => boolean;
    }> = [
      {
        id: 'consulta-registro',
        label: 'Créditos Registrados',
        link: 'consulta-caracteristicas',
        isAllowed: () => this.hasRegistrationLink('consulta-caracteristicas'),
      },
      {
        id: 'consulta-operacao',
        label: 'Operações Realizadas',
        link: 'consulta-operacoes',
        isAllowed: () => this.hasOperationLink('consulta-operacoes'),
      },
      {
        id: 'consulta-posicao',
        label: 'Posição de Créditos',
        link: 'consulta-posicao',
        isAllowed: () => this.hasPositionLink('consulta-posicao'),
      },
    ];

    const allowedLinks = consultationLinks.filter(item => item.isAllowed());
    if (!allowedLinks.length) {
      return;
    }

    children.push(this.sectionItem('consultas-section', 'Consultas'));
    allowedLinks.forEach(item => {
      children.push(this.toLinkItem(item.id, item.label, item.link));
    });
  }

  private hasOperationLink(link: string): boolean {
    return this.operacaoCreditoChildren.some(item => item.link === link);
  }

  private hasRegistrationLink(link: string): boolean {
    return this.registroCreditoChildren.some(item => item.link === link);
  }

  private hasPositionLink(link: string): boolean {
    return this.posicaoCreditoChildren.some(item => item.link === link);
  }

  private buildProjectsEmissionGroup(): CarSidebarGroupItem | null {
    const children: CarSidebarChildItem[] = [];

    this.appendProjectCreditSection(children);
    this.appendEmissionCreditSection(children);

    if (!children.length) {
      return null;
    }

    return {
      type: 'group',
      id: 'projects-emission',
      label: 'Registro de projetos e emissão de créditos',
      icon: 'verified',
      expanded: true,
      children,
    };
  }

  private appendProjectCreditSection(children: CarSidebarChildItem[]): void {
    const projectLinks: Array<{
      id: string;
      label: string;
      link: string;
      isAllowed: () => boolean;
    }> = [
      {
        id: 'projeto-registro',
        label: 'Registrar',
        link: 'registro-projeto',
        isAllowed: () => this.hasProjectLink('registro-projeto'),
      },
      {
        id: 'projeto-validacao',
        label: 'Validar',
        link: 'validacao-projeto-credito',
        isAllowed: () => this.hasProjectLink('validacao-projeto-credito'),
      },
      {
        id: 'projeto-consulta',
        label: 'Consulta',
        link: 'consulta-projeto',
        isAllowed: () => this.hasProjectLink('consulta-projeto'),
      },
    ];

    const allowedLinks = projectLinks.filter(item => item.isAllowed());
    if (!allowedLinks.length) {
      return;
    }

    children.push(this.sectionItem('projeto-credito-section', 'Projeto de Crédito'));
    allowedLinks.forEach(item => {
      children.push(this.toLinkItem(item.id, item.label, item.link));
    });
  }

  private appendEmissionCreditSection(children: CarSidebarChildItem[]): void {
    const emissionLinks: Array<{
      id: string;
      label: string;
      link: string;
      isAllowed: () => boolean;
    }> = [
      {
        id: 'emissao-registro',
        label: 'Emissão',
        link: 'registro-emissao-credito',
        isAllowed: () => this.hasEmissionLink('registro-emissao-credito'),
      },
      {
        id: 'emissao-verificacao',
        label: 'Verificação',
        link: 'confirma-emissao-credito',
        isAllowed: () => this.hasEmissionLink('confirma-emissao-credito'),
      },
      {
        id: 'emissao-consulta',
        label: 'Consulta',
        link: 'consulta-emissao-credito',
        isAllowed: () => this.hasEmissionLink('consulta-emissao-credito'),
      },
    ];

    const allowedLinks = emissionLinks.filter(item => item.isAllowed());
    if (!allowedLinks.length) {
      return;
    }

    children.push(this.sectionItem('emissao-credito-section', 'Emissão de Créditos'));
    allowedLinks.forEach(item => {
      children.push(this.toLinkItem(item.id, item.label, item.link));
    });
  }

  hasCreditStockPermission(): boolean {
    return this.hasPositionLink('consulta-posicao');
  }

  hasProjectConsultPermission(): boolean {
    return this.hasProjectLink('consulta-projeto');
  }

  private hasProjectLink(link: string): boolean {
    return this.registroProjetoChildren.some(item => item.link === link);
  }

  private hasEmissionLink(link: string): boolean {
    return this.emissaoCreditoChildren.some(item => item.link === link);
  }

  private buildFooterItems(): CarSidebarFooterItem[] {
    const supportUrl = this.environment?.urlSuporteB3
      ?? 'https://atendimento.b3.com.br/atendeb3';

    return [
      {
        id: 'support',
        label: 'Ajuda e suporte',
        icon: 'help',
        action: 'support',
        url: supportUrl,
        target: '_blank',
      },
      {
        id: 'logout',
        label: 'Sair',
        icon: 'logout',
        action: 'logout',
      },
    ];
  }

  getHomeActionColumns(): HomeActionColumn[] {
    const nav = this.sidebarNav$.getValue();
    if (!nav) {
      return [];
    }

    return nav.groups
      .map(group => this.buildHomeActionColumn(group))
      .filter((column): column is HomeActionColumn => column !== null);
  }

  getHomeSearchGroups(): HomeSearchGroup[] {
    const allowedLinks = this.getAllowedHomeLinks();

    return HOME_SEARCH_GROUPS
      .map(group => ({
        id: group.id,
        title: group.title,
        items: group.links
          .map(link => this.toHomeSearchActionItem(link, allowedLinks.get(link)))
          .filter((item): item is HomeActionItem => !!item),
      }))
      .filter(group => group.items.length > 0);
  }

  private buildHomeActionColumn(group: CarSidebarGroupItem): HomeActionColumn | null {
    const subsections = this.buildHomeSubsections(group);
    if (!subsections.length) {
      return null;
    }

    return {
      id: group.id,
      title: HOME_COLUMN_TITLES[group.id] ?? group.label,
      subsections,
    };
  }

  private buildHomeSubsections(group: CarSidebarGroupItem): HomeActionSubsection[] {
    const subsections: HomeActionSubsection[] = [];
    let currentSubsection: HomeActionSubsection | null = null;

    group.children.forEach(child => {
      if (child.type === 'section') {
        currentSubsection = {
          id: child.id,
          title: child.label,
          items: [],
        };
        subsections.push(currentSubsection);
        return;
      }

      if (child.type !== 'link' || HOME_EXCLUDED_LINKS.has(child.link)) {
        return;
      }

      const item = this.toHomeActionItem(child);
      if (!item) {
        return;
      }

      if (!currentSubsection) {
        currentSubsection = {
          id: `${group.id}-default`,
          title: group.label,
          items: [],
        };
        subsections.push(currentSubsection);
      }

      currentSubsection.items.push(item);
    });

    return subsections.filter(subsection => subsection.items.length > 0);
  }

  private getAllowedHomeLinks(): Map<string, HomeActionItem> {
    const nav = this.sidebarNav$.getValue();
    const allowedLinks = new Map<string, HomeActionItem>();

    nav?.groups.forEach(group => {
      group.children.forEach(child => {
        if (child.type !== 'link' || HOME_EXCLUDED_LINKS.has(child.link)) {
          return;
        }

        const item = this.toHomeActionItem(child);
        if (item) {
          allowedLinks.set(child.link, item);
        }
      });
    });

    return allowedLinks;
  }

  private toHomeActionItem(linkItem: CarSidebarLinkItem): HomeActionItem | null {
    const presentation = HOME_LINK_PRESENTATION[linkItem.link];
    if (!presentation) {
      return null;
    }

    return {
      id: linkItem.id,
      label: presentation.label ?? linkItem.label,
      link: linkItem.link,
      icon: presentation.icon,
    };
  }

  private toHomeSearchActionItem(link: string, allowedItem?: HomeActionItem): HomeActionItem | null {
    if (!allowedItem) {
      return null;
    }

    const presentation = HOME_SEARCH_LINK_PRESENTATION[link];
    if (!presentation) {
      return null;
    }

    return {
      id: allowedItem.id,
      label: presentation.label,
      link,
      icon: presentation.icon,
    };
  }

  private sectionItem(id: string, label: string): CarSidebarSectionItem {
    return { type: 'section', id, label };
  }

  private toLinkItem(id: string, label: string, link: string): CarSidebarLinkItem {
    return { type: 'link', id, label, link };
  }

  private toLegacyMenuItems(nav: CarSidebarNav): B3MenuItem[] {
    return nav.groups.map(group => ({
      title: group.label,
      expanded: group.expanded,
      children: group.children
        .filter((child): child is CarSidebarLinkItem => child.type === 'link')
        .map(child => ({ title: child.label, link: child.link })),
    }));
  }
}
